/* imageFrame - the style for an Image element's frame.
 *
 * 0.42.39 - One function for the page AND the admin preview, so the two
 * cannot draw different frames.
 *
 * BORDER, not a stroke. The platform's cards use an inset box-shadow as
 * their "stroke" so that a hover state does not change their size. A
 * frame is different in two ways: it is a fixed setting, not a hover, so
 * taking space is fine - and it has INNER PADDING. An inset shadow is
 * painted inside the box, over the padding; once thicker than the
 * padding it would paint over the image's edge. A border is laid out
 * outside the padding and never overlaps the picture.
 *
 * Applied to the <img> itself rather than a wrapper, because the image
 * hugs its natural size: a frame on a wrapper would stretch across the
 * whole column around a smaller picture. box-sizing: border-box keeps
 * the image's max-width: 100% true of the FRAMED box, so a frame never
 * pushes the image out of its column.
 *
 *   frame_width    px, 0 = no frame
 *   frame_color    a Henaket token or a custom #RRGGBB (HenaketPalettePicker); empty = default
 *   frame_padding  px, equal on all four sides
 */

const clamp = (v, lo, hi) => {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return 0;
  return Math.min(hi, Math.max(lo, n));
};

// The picker's "Default" swatch previews text-800, so default IS
// text-800 - a swatch that shows one colour and draws another would be
// a lie in the admin's own tool.
const DEFAULT_TOKEN = '--color-text-800';

export function imageFrameStyle(el) {
  const width = clamp(el && el.frame_width, 0, 16);
  if (width === 0) return {};
  const padding = clamp(el && el.frame_padding, 0, 64);
  // Only a CSS custom property is accepted - the picker only ever gives
  // one, and anything else in the stored value is ignored rather than
  // written into a style.
  const raw = (el && el.frame_color) || '';
  // 0.47.13 - Or a custom #RRGGBB (the palette picker's Custom).
  const color = /^--[a-z0-9-]+$/i.test(raw) ? `var(${raw})`
    : /^#[0-9a-f]{6}$/i.test(raw) ? raw : `var(${DEFAULT_TOKEN})`;
  return {
    border: `${width}px solid ${color}`,
    padding: `${padding}px`,
    /* 0.43.32 - Rounded corners, 4px, when the frame asks for them.
     *
     * On the FRAME, and the image inside clipped to match: rounding the
     * border alone leaves the picture's square corners poking through
     * it. The inner radius is the outer one less the border and the
     * padding, floored at 0 - the geometry a rounded frame needs to
     * look like one shape rather than two.
     *
     * Only with a frame. A radius on a bare image rounds the picture
     * itself, which is a different decision. */
    ...(el && el.frame_rounded
      ? { borderRadius: '4px',
          overflow: 'hidden' }
      : null),
    boxSizing: 'border-box',
  };
}

/* 0.46.15 - The picture's size, set as ONE of three pairs so they
 * cannot contradict each other (a fixed area of 300 x 200 with a
 * largest height of 100, say):
 *
 *   size_mode 'own'   its own size, within the column (the default)
 *   size_mode 'max'   max_width and/or max_height: never larger, keeps
 *                     its shape. Either one alone works.
 *   size_mode 'area'  area_width and area_height: the picture sits in
 *                     that box at its own shape (object-fit: contain).
 *                     Both are needed, until then it is its own size.
 *
 * One function for the page and the admin preview. The column always
 * wins: max-width 100% stays.
 *
 * Elements saved before 0.46.15 have no size_mode: both area numbers
 * make an area, and otherwise max_height and a lone area number are
 * read as the largest size (what 0.46.14 drew). */
const _px = (v) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : 0);

export function imageSizeModeOf(e) {
  const m = e && e.size_mode;
  if (m === 'own' || m === 'max' || m === 'area' || m === 'fill') return m;
  if (_px(e && e.area_width) && _px(e && e.area_height)) return 'area';
  if (_px(e && e.max_height) || _px(e && e.max_width)
      || _px(e && e.area_width) || _px(e && e.area_height)) return 'max';
  return 'own';
}

/* The two numbers of the 'max' pair, legacy values folded in. */
export function imageMaxOf(e) {
  if (e && e.size_mode === 'max') {
    return { w: _px(e.max_width), h: _px(e.max_height) };
  }
  const h = _px(e && e.max_height);
  const ah = _px(e && e.area_height);
  return {
    w: _px(e && e.max_width) || _px(e && e.area_width),
    h: h && ah ? Math.min(h, ah) : (h || ah),
  };
}

export function imageSizeStyle(e) {
  const mode = imageSizeModeOf(e);
  if (mode === 'area') {
    const w = _px(e.area_width);
    const h = _px(e.area_height);
    if (w && h) {
      // 0.47.26 - Picture fit: the whole picture (contain, the default)
      // or fill and crop (cover: the shorter side fits the area, the
      // longer is cut equally at both ends).
      return { maxWidth: '100%', width: `${w}px`, height: `${h}px`, objectFit: e.image_fit === 'cover' ? 'cover' : 'contain' };
    }
  }
  // 0.47.35 - As wide as its column (smaller pictures are enlarged),
  // keeping its shape, or at a fixed height (the picture fit decides
  // whether it is cropped or shown whole).
  if (mode === 'fill') {
    const h = _px(e.fill_height);
    return h
      ? { width: '100%', maxWidth: '100%', height: `${h}px`, objectFit: e.image_fit === 'cover' ? 'cover' : 'contain' }
      : { width: '100%', maxWidth: '100%', height: 'auto' };
  }
  if (mode === 'max') {
    const { w, h } = imageMaxOf(e);
    return {
      maxWidth: w ? `min(100%, ${w}px)` : '100%',
      maxHeight: h ? `${h}px` : undefined,
      width: 'auto',
      height: 'auto',
    };
  }
  return { maxWidth: '100%', width: 'auto', height: 'auto' };
}

/* 0.46.08 - Where an Image element sits across its column: left (the
 * default), center or right. The picture is a block, so its side
 * margins place it. In a fixed area the picture is drawn inside the
 * box at its own shape; object-position puts it on the same side, as
 * the browser otherwise centres it (which looked shifted to the right
 * when the area was wider than the picture). */
export function imageAlignOf(e) {
  const a = e && e.align;
  if (a === 'left' || a === 'center' || a === 'right') return a;
  // 0.46.10 - Unset follows the element's position on the page, so an
  // image placed at the centre or right of the page is drawn there
  // too, not at the left of its centred column.
  const p = e && e.page_position;
  return p === 'center' || p === 'right' ? p : 'left';
}
export function imageAlignStyle(e) {
  const a = imageAlignOf(e);
  return {
    marginLeft: a === 'left' ? 0 : 'auto',
    marginRight: a === 'right' ? 0 : 'auto',
    // 0.47.12 - A round picture shows its middle: the circle itself
    // is placed by the margins, the cut is always centered.
    objectPosition: imageCornersOf(e) === 'round' || e.image_fit === 'cover' ? 'center' : `${a} center`,
  };
}

/* 0.46.12 - The element's position on the page moved to
 * lib/elementPosition.js (every element has it now). */
export { elementPositionOf, elementPositionStyle } from './elementPosition';

/* 0.47.12 - The picture's corners, with or without a frame:
 *
 *   img_corners 'square' (the default), 'rounded' or 'round'
 *   img_radius  px, for 'rounded' (8 by default)
 *
 * 'round' makes a circle: the picture is cut to a square (the middle of
 * it, object-fit: cover) and fully rounded. In a fixed area the circle's
 * size is the area's smaller side. A frame and a shadow follow the same
 * shape.
 *
 * Elements saved before 0.47.12 with a frame and "Round the corners"
 * (frame_rounded) read as 'rounded' at 4px, as they were drawn.
 *
 * Spread AFTER imageFrameStyle and imageSizeStyle: it replaces the
 * frame's 4px and, for a circle, the area's shape. */
export function imageCornersOf(e) {
  const c = e && e.img_corners;
  if (c === 'square' || c === 'rounded' || c === 'round') return c;
  return e && e.frame_rounded && Number(e.frame_width) > 0 ? 'rounded' : 'square';
}
export function imageRadiusOf(e) {
  const n = Math.round(Number(e && e.img_radius));
  if (Number.isFinite(n) && n >= 0 && (e.img_radius !== '' && e.img_radius != null)) return Math.min(n, 500);
  return e && !e.img_corners && e.frame_rounded ? 4 : 8;
}
export function imageCornerStyle(e) {
  const c = imageCornersOf(e);
  if (c === 'square') return {};
  if (c === 'rounded') {
    const r = imageRadiusOf(e);
    return r ? { borderRadius: `${r}px`, overflow: 'hidden' } : {};
  }
  const out = { borderRadius: '50%', overflow: 'hidden', objectFit: 'cover' };
  if (imageSizeModeOf(e) === 'area' && _px(e.area_width) && _px(e.area_height)) {
    const s = Math.min(_px(e.area_width), _px(e.area_height));
    out.width = `${s}px`; out.height = `${s}px`;
  } else if (imageSizeModeOf(e) === 'fill' && _px(e.fill_height)) {
    // 0.47.35 - A circle as tall as the height, never wider than the column.
    out.width = `${_px(e.fill_height)}px`; out.height = `${_px(e.fill_height)}px`;
  } else {
    out.aspectRatio = '1 / 1';
  }
  return out;
}

/* 0.47.12 - A round picture at its own size (or a largest size) is as
 * wide as its SHORTER side, so a 300 x 200 picture becomes a 200px
 * circle rather than being enlarged to 300. CSS cannot know the
 * picture's own size, so it is read once the picture has loaded. The
 * column and a largest size still apply (max-width / max-height). */
export function roundImageOnLoad(e) {
  if (imageCornersOf(e) !== 'round') return undefined;
  if (imageSizeModeOf(e) === 'area' && _px(e.area_width) && _px(e.area_height)) return undefined;
  // 0.47.35 - Column width: the circle is as wide as the column.
  if (imageSizeModeOf(e) === 'fill') return undefined;
  return (ev) => {
    const img = ev && ev.currentTarget;
    if (!img) return;
    let s = Math.min(img.naturalWidth || 0, img.naturalHeight || 0);
    // A largest width or height keeps the circle within it too.
    if (imageSizeModeOf(e) === 'max') {
      const { w, h } = imageMaxOf(e);
      if (w) s = Math.min(s, w);
      if (h) s = Math.min(s, h);
    }
    if (s > 0) img.style.width = `${s}px`;
  };
}

/* 0.47.37 - Space around the picture: Above, Right, Below and Left, each
 * a Henaket space step (img_space_top / _right / _bottom / _left), set
 * on the figure, so it is outside the frame and the shadow. Independent
 * of the element's Space below (the gap to the next element). */
const IMG_SPACE_STEPS = new Set([1, 2, 3, 4, 5, 6, 8, 10, 12, 16, 20]);
export const IMAGE_SPACE_SIDES = [['top', 'Above'], ['right', 'Right'], ['bottom', 'Below'], ['left', 'Left']];
export function imageSpaceStyle(e) {
  const out = {};
  for (const [side] of IMAGE_SPACE_SIDES) {
    const v = e && e[`img_space_${side}`];
    if (IMG_SPACE_STEPS.has(v)) out[`padding${side[0].toUpperCase()}${side.slice(1)}`] = `var(--space-${v})`;
  }
  return out;
}
